import React, {
    useState, useCallback, useEffect, useMemo,
} from 'react';
import { NSSlideout } from 'bricks';
import { useGetDocument, useUpdateDocument } from 'views/DocumentLibrary/hooks';
import { IDealDocument, IUpdateDocumentRequest } from 'views/DocumentLibrary/types';
import './DocumentLibrarySlideout.scss';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from 'services/urls';
import { useParams } from 'react-router-dom';
import useToast from 'hooks/useToast';
import { formatUTCtoLocal, formatFileSize } from 'ns_libs/formatter';
import DocumentDetails from './DocumentDetails';
import DocumentPreview from './DocumentPreview';
import DocumentLibraryHeader from './DocumentLibraryHeader';

interface DocumentLibrarySlideoutProps {
    isOpen: boolean;
    toggle: () => void;
    documentId: number;
    onDelete: (document: IDealDocument) => void;
    editMode: boolean;
    setEditMode: (editMode: boolean) => void;
}

const DocumentLibrarySlideout: React.FC<DocumentLibrarySlideoutProps> = ({
    isOpen, toggle, documentId, onDelete, editMode, setEditMode,
}) => {
    const queryClient = useQueryClient();
    const { dealId } = useParams<{ dealId: string }>();
    const { showSuccess, showError } = useToast();

    const { data: document } = useGetDocument({ documentId, options: { enabled: Boolean(isOpen && documentId) } });

    const [docDetails, setDocDetails] = useState<IUpdateDocumentRequest>({});

    const { mutate: updateDocument } = useUpdateDocument();

    useEffect(() => {
        if (!document) return;
        const updateDocumentForm = {
            name: document.name,
            directoryId: document.directoryId,
            type: document.type || undefined,
            activities: document.timelines?.map(activity => activity.dealTimelineId) || [],
        };
        if (document.type) updateDocumentForm.type = document.type;
        setDocDetails(updateDocumentForm);
    }, [document]);

    const handleOpenDocument = useCallback(() => {
        if (document?.s3Url) {
            window.open(document?.s3Url, '_blank');
        }
    }, [document?.s3Url]);

    const handleSaveDocument = useCallback(() => {
        const data = {
            name: docDetails.name,
            type: docDetails.type,
            directoryId: docDetails.directoryId || null,
            activities: docDetails.activities,
        };
        updateDocument({ documentId, data },
            {
                onSuccess: () => {
                    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DOCUMENT_DETAIL], documentId });
                    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DEAL_DIRECTORY_TREE, Number(dealId)] });
                    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DOCUMENTS] });
                    showSuccess('Document updated successfully');
                },
                onError: () => {
                    showError('Failed to update document. Please try again.');
                },
            });
        setEditMode(!editMode);
    }, [documentId, docDetails]);

    const handleDeleteDocument = useCallback(() => {
        if (document) onDelete(document);
        toggle();
    }, [document]);

    const FilePreview = useMemo(() => (
        <DocumentPreview
            key={`preview-${documentId}-${document?.s3Url}`}
            uri={document?.s3Url}
            format={document?.extension?.toLowerCase()}
            handleOpenDocument={handleOpenDocument}
        />
    ),
    [documentId, document?.s3Url, document?.extension, handleOpenDocument]);

    return (
        <NSSlideout
            customHeader={(
                <DocumentLibraryHeader
                    name={document?.name || ''}
                    size={document?.size ? formatFileSize(document.size) : ''}
                    toggle={toggle}
                    editMode={editMode}
                    toggleEditMode={() => setEditMode(!editMode)}
                    handleSaveDocument={handleSaveDocument}
                    handleOpenDocument={handleOpenDocument}
                    handleDeleteDocument={handleDeleteDocument}
                />
            )}
            size="xl"
            isOpen={isOpen}
            toggle={toggle}
            modalBodyClassName="DocumentLibrarySlideout p-0"
        >
            {document && (
                <div className="d-flex h-100">
                    {FilePreview}
                    <DocumentDetails
                        editMode={editMode}
                        documentName={document.name}
                        folder={document.directoryName || 'Home'}
                        format={document.extension}
                        size={document?.size ? formatFileSize(document.size) : undefined}
                        dateModified={formatUTCtoLocal(document.updatedAt, 'MMM d, yyyy, h:mm a')}
                        documentType={document.type || undefined}
                        activities={document.timelines || undefined}
                        updateDocumentForm={docDetails}
                        setUpdateDocumentForm={setDocDetails}
                    />
                </div>
            )}
        </NSSlideout>
    );
};

export default DocumentLibrarySlideout;
